import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import SiteType from "../types/siteType";
import SearchResults from "../types/searchResults";
import SearchParams, { defaultParams } from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";

interface SiteTypeState {
  siteType: SiteType;
  siteTypeSearchResults?: SearchResults;
  siteTypeFilter: SearchParams;
  status: "idle" | "loading" | "failed";
}

const initialState: SiteTypeState = {
  siteType: {
    id: 0,
    name: undefined,
  },
  siteTypeFilter: defaultParams,
  siteTypeSearchResults: undefined,
  status: "idle",
};

export const getSiteTypeId = createAsyncThunk<SiteType, number>(
  "site-type/get/id",
  async (id: number) => {
    const response = await http.get<SiteType>(`/sitetype/${id}`);
    return response.data;
  }
);

export const editSiteType = createAsyncThunk<SiteType, SiteType>(
  "site-type/edit",
  async (siteType: SiteType) => {
    const response = await http.put<SiteType>(
      `/sitetype/${siteType.id}`,
      siteType,
      {
        params: {
          successToast: true,
        },
      }
    );
    return response.data;
  }
);

export const deleteSiteType = createAsyncThunk<SiteType, number>(
  "site-type/delete",
  async (id: number) => {
    const response = await http.delete<SiteType>(`/sitetype/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const postSiteType = createAsyncThunk<SiteType, SiteType>(
  "site-type/post",
  async (siteType: SiteType) => {
    const response = await http.post<SiteType>(`/sitetype`, siteType, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const searchSiteType = createAsyncThunk<SearchResults, SearchParams>(
  "site-type/search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(`/sitetype/search?${qs}`);
    return response.data;
  }
);

const siteTypeSlice = createSlice({
  name: "siteType",
  initialState,
  reducers: {
    resetSiteType(state) {
      state.siteType = { ...initialState.siteType };
    },
    setSiteTypeFilter(state, action) {
      state.siteTypeFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSiteTypeId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSiteTypeId.fulfilled, (state, action) => {
        state.status = "idle";
        state.siteType = action.payload;
      })
      .addCase(getSiteTypeId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editSiteType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editSiteType.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(editSiteType.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteSiteType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteSiteType.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteSiteType.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postSiteType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postSiteType.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(postSiteType.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchSiteType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchSiteType.fulfilled, (state, action) => {
        state.status = "idle";
        state.siteTypeSearchResults = action.payload;
      })
      .addCase(searchSiteType.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default siteTypeSlice.reducer;
export const { resetSiteType, setSiteTypeFilter } = siteTypeSlice.actions;
